import type { NewsletterVariant } from "lib/types/sitewideOption";
import { ZEPHR_FEATURES } from "lib/zephr/constants";
import { Section, useZephrForm } from "../useZephrForm";
import { useTracker } from "../ZephrTracker";
import { ZephrFeature } from "../zephrFeature";
import {
  applyCreateAccountRequirements,
  CHECKOUT_REQUIRED_FIELDS,
  CREATE_ACCOUNT_REQUIRED_FIELDS,
} from "./registrationFields";
import styles from "./LoginRegistrationForm.module.scss";

interface LoginRegistrationFormProps {
  newslettersList: NewsletterVariant[];
  variant?: "page" | "modal";
  formType?: "regular" | "checkout";
  selector?: string;
  onAuthComplete?: () => void;
}

export const LoginRegistrationForm = ({
  newslettersList,
  variant = "page",
  formType = "regular",
  selector,
  onAuthComplete,
}: LoginRegistrationFormProps) => {
  const tracker = useTracker();
  const isCheckout = formType === "checkout";
  const featureName = isCheckout
    ? ZEPHR_FEATURES.CHECKOUT_REGISTRATION_FORM
    : ZEPHR_FEATURES.LOGIN_REGISTRATION_FORM;

  useZephrForm({
    selector,
    initiallyRequiredFields: isCheckout
      ? CHECKOUT_REQUIRED_FIELDS
      : CREATE_ACCOUNT_REQUIRED_FIELDS,
    validateOnLoad: true,
    onLoad: (fields) =>
      applyCreateAccountRequirements(fields, { checkout: isCheckout }),
    onUpdate: (section, fields) => {
      if (section === Section.CREATE_YOUR_ACCOUNT) {
        applyCreateAccountRequirements(fields, { checkout: isCheckout });
      }
    },
    onSuccess: (authType, values) => {
      tracker.trackAuth(authType, values);

      if (onAuthComplete) {
        onAuthComplete();
        return;
      }

      const redirectUrl =
        new URLSearchParams(window.location.search).get("redirectUrl") ?? "/";

      // A real navigation (not history.replaceState) is required here: Next's
      // router never learns about a raw replaceState call, so ModalProvider
      // keeps reading router.pathname as "/members/login" and never re-checks
      // zephrModal.isPending() on the actual redirect target - the pending
      // checkout modal then only opens if something else happens to reload
      // the page, which is unreliable across browsers.
      window.location.assign(redirectUrl);
    },
  });

  const getNewsletterImageUrl = (newsletterType: string): string => {
    const newsletter = newslettersList?.find(
      (nl) => nl.newsletterType === newsletterType
    );
    return newsletter?.cardImage?.url
      ? `url(${newsletter.cardImage.url}?auto=format&fit=max&h=50&q=80)`
      : "";
  };

  // Variables without an image are left out: React renders an empty custom
  // property differently on the server and the client, which caused a
  // hydration mismatch.
  const newsLetterStyles: { [key: string]: string } = Object.fromEntries(
    Object.entries({
      "--fintech": getNewsletterImageUrl("fintech"),
      "--sustainability": getNewsletterImageUrl("climatetech"),
      "--futureproof": getNewsletterImageUrl("upround"),
      "--startup-life": getNewsletterImageUrl("startuplife"),
      "--deeptech": getNewsletterImageUrl("deeptech"),
    }).filter(([, value]) => value)
  );

  const formCard = (
    <div
      className={
        variant === "modal"
          ? "w-full bg-white"
          : "w-[98vw] max-w-[360px] bg-white px-5 pb-2 pt-4 md:w-[360px]"
      }
      role="form"
    >
      <ZephrFeature name={featureName} />
    </div>
  );

  return (
    <div className={styles.loginRegistrationForm} style={newsLetterStyles}>
      {variant === "modal" ? (
        formCard
      ) : (
        <div className="md:mt-1 lg:mt-[85px]">{formCard}</div>
      )}
    </div>
  );
};
